import { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus,
  Loader2,
  Users,
  ClipboardCheck,
  CalendarDays,
  MapPin,
  ChevronRight,
  ArrowLeft,
  Save,
  UserCheck,
  PlusCircle,
  Check,
  AlertTriangle,
  Download,
  Trash2,
  CheckCircle2
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import SeletivaProvaOperacao from './SeletivaProvaOperacao';

const formInicial = {
  nome: '',
  descricao: '',
  sexo: 'misto',
  categoria: '',
  ano_nascimento_inicio: '',
  ano_nascimento_fim: '',
  data: '',
  local: ''
};

function nomeSexo(valor) {
  if (valor === 'masculino') return 'Masculino';
  if (valor === 'feminino') return 'Feminino';
  return 'Misto';
}

function nomeStatus(valor) {
  const mapa = {
    rascunho: 'Rascunho',
    ativa: 'Ativa',
    em_andamento: 'Em andamento',
    finalizada: 'Finalizada'
  };

  return mapa[valor] || valor || 'Rascunho';
}

export default function SeletivasPage() {
  const { user, effectiveRole } = useAuth();

  const [seletivas, setSeletivas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selecionada, setSelecionada] = useState(null);
  const [acaoSeletiva, setAcaoSeletiva] = useState(null);
  const [painel, setPainel] = useState(null);
  const [loadingPainel, setLoadingPainel] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(formInicial);

  const [aba, setAba] = useState('provas');

  const [alunosAtivos, setAlunosAtivos] = useState([]);
  const [participantesExternos, setParticipantesExternos] = useState([]);
  const [participanteIds, setParticipanteIds] = useState([]);
  const [savingParticipantes, setSavingParticipantes] = useState(false);
  const [buscaParticipante, setBuscaParticipante] = useState('');

  const [externoDialog, setExternoDialog] = useState(false);
  const [savingExterno, setSavingExterno] = useState(false);
  const [externoForm, setExternoForm] = useState({
    nome: '',
    data_nascimento: '',
    sexo: '',
    telefone: '',
    observacao: ''
  });

  const [provas, setProvas] = useState([]);
  const [avaliadores, setAvaliadores] = useState([]);
  const [provaOperacao, setProvaOperacao] = useState(null);

  const [provaDialog, setProvaDialog] = useState(false);
  const [savingProva, setSavingProva] = useState(false);

  const [provaForm, setProvaForm] = useState({
    nome: '',
    descricao: '',
    instrucoes: '',
    tipo_resultado: 'tempo',
    unidade: 's',
    criterio_melhor: 'menor',
    numero_tentativas: 1,
    conceitos: 'Excelente,Bom,Regular,Ruim',
    nota_minima: 0,
    nota_maxima: 10,
    avaliador_ids: []
  });

  const termoParticipante =
    buscaParticipante.trim().toLowerCase();

  const listaParticipantes = [
    ...alunosAtivos.map(
      (aluno) => ({
        ...aluno,
        tipo_participante: 'aluno'
      })
    ),
    ...participantesExternos
  ]
    .filter((item, indice, lista) => {
      const id = item.id || item.aluno_id;

      return (
        id &&
        lista.findIndex(
          (outro) =>
            (outro.id || outro.aluno_id) === id
        ) === indice
      );
    })
    .filter((item) => {
      if (!termoParticipante) return true;

      const nome = String(
        item.name ||
        item.aluno_nome ||
        ''
      ).toLowerCase();

      return nome.includes(
        termoParticipante
      );
    });

  const podeAdministrar =
    ['gestor', 'socio'].includes(user?.role) &&
    effectiveRole !== 'professor_staff';

  const carregar = async () => {
    setLoading(true);

    try {
      const response = await api.get('/seletivas');
      setSeletivas(
        Array.isArray(response.data)
          ? response.data
          : response.data?.seletivas || []
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const finalizarSeletiva = async () => {
    if (!selecionada?.id) return;

    const confirmar = window.confirm(
      `Deseja realmente finalizar a seletiva "${selecionada.nome}"?\n\n` +
      'Depois de finalizada, ela ficará marcada como encerrada.'
    );

    if (!confirmar) return;

    setAcaoSeletiva('finalizar');

    try {
      await api.post(
        `/seletivas/${selecionada.id}/finalizar`
      );

      setSelecionada((atual) => ({
        ...atual,
        status: 'finalizada'
      }));

      setSeletivas((lista) =>
        lista.map((item) =>
          item.id === selecionada.id
            ? {
                ...item,
                status: 'finalizada'
              }
            : item
        )
      );

      toast.success(
        'Seletiva finalizada com sucesso'
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcaoSeletiva(null);
    }
  };

  const apagarSeletiva = async () => {
    if (!selecionada?.id) return;

    const confirmar = window.confirm(
      `Apagar a seletiva "${selecionada.nome}"?\n\n` +
      'Ela desaparecerá da lista de seletivas. ' +
      'Os dados históricos permanecerão preservados no sistema.'
    );

    if (!confirmar) return;

    const confirmarNovamente = window.confirm(
      'Confirme novamente: deseja realmente apagar esta seletiva?'
    );

    if (!confirmarNovamente) return;

    setAcaoSeletiva('apagar');

    try {
      await api.delete(
        `/seletivas/${selecionada.id}`
      );

      toast.success(
        'Seletiva apagada com sucesso'
      );

      setSelecionada(null);
      setPainel(null);
      setProvaOperacao(null);

      await carregar();
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setAcaoSeletiva(null);
    }
  };

  const exportarExcel = async () => {
    if (!selecionada?.id) return;

    try {
      const response = await api.get(
        `/seletivas/${selecionada.id}/exportar-excel`,
        {
          responseType: 'blob'
        }
      );

      const blob = new Blob(
        [response.data],
        {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      );

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      const disposition =
        response.headers?.['content-disposition'] || '';

      let nomeArquivo =
        `seletiva_${selecionada.nome || 'resultado'}.xlsx`;

      const match = disposition.match(
        /filename="?([^"]+)"?/i
      );

      if (match?.[1]) {
        nomeArquivo = match[1];
      }

      link.href = url;
      link.setAttribute(
        'download',
        nomeArquivo
      );

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success(
        'Planilha gerada com sucesso'
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    }
  };

  const abrirSeletiva = async (seletiva) => {
    setSelecionada(seletiva);
    setLoadingPainel(true);

    try {
      const response = await api.get(
        `/seletivas/${seletiva.id}/painel`
      );

      setPainel(response.data);

      await carregarDetalhes(
        seletiva
      );
    } catch (err) {
      setPainel(null);
      toast.error(formatApiError(err));
    } finally {
      setLoadingPainel(false);
    }
  };


  const normalizarLista = (data, chaves = []) => {
    if (Array.isArray(data)) return data;

    for (const chave of chaves) {
      if (Array.isArray(data?.[chave])) {
        return data[chave];
      }
    }

    return [];
  };

  const carregarDetalhes = async (seletiva) => {
    if (!seletiva?.id) return;

    try {
      const requests = [
        api.get(`/seletivas/${seletiva.id}/provas`)
      ];

      if (podeAdministrar) {
        requests.push(
          api.get(`/seletivas/${seletiva.id}/alunos-ativos`),
          api.get(`/seletivas/${seletiva.id}/participantes`),
          api.get('/gestor/colaboradores'),
          api.get('/gestor/alunos')
        );
      }

      const respostas = await Promise.all(requests);

      const provasResp = respostas[0];

      setProvas(
        normalizarLista(
          provasResp.data,
          ['provas', 'itens']
        )
      );

      if (podeAdministrar) {
        const alunosAtivosResp = respostas[1];
        const participantesResp = respostas[2];
        const colaboradoresResp = respostas[3];
        const alunosResp = respostas[4];

        setAlunosAtivos(
          normalizarLista(
            alunosAtivosResp.data,
            ['alunos', 'itens']
          )
        );

        const participantesLista =
          normalizarLista(
            participantesResp.data,
            [
              'participantes',
              'alunos'
            ]
          );

        setParticipanteIds(
          participantesLista
            .filter(
              (p) =>
                p.ativo !== false
            )
            .map(
              (p) =>
                p.aluno_id ||
                p.id
            )
            .filter(Boolean)
        );

        setParticipantesExternos(
          participantesLista
            .filter(
              (p) =>
                p.ativo !== false &&
                (
                  p.tipo_participante === 'externo' ||
                  String(
                    p.aluno_id || ''
                  ).startsWith('externo:')
                )
            )
            .map(
              (p) => ({
                ...p,
                id: p.aluno_id || p.id,
                name:
                  p.aluno_nome ||
                  p.name ||
                  'Atleta externo',
                tipo_participante: 'externo'
              })
            )
        );

        const colaboradores =
          normalizarLista(
            colaboradoresResp.data,
            [
              'colaboradores',
              'usuarios'
            ]
          );

        const alunos =
          normalizarLista(
            alunosResp.data,
            ['alunos']
          );

        const profs = colaboradores
          .filter(
            (item) =>
              item.role === 'professor' ||
              item.tipo_colaborador === 'professor'
          )
          .map(
            (item) => ({
              ...item,
              _tipo: 'Professor'
            })
          );

        const staffs = alunos
          .filter(
            (item) =>
              item.acesso_staff === true ||
              item.plano_acesso_staff === true ||
              String(
                item.plano_nome ||
                item.nome_plano ||
                ''
              )
                .toUpperCase()
                .includes('STAFF')
          )
          .map(
            (item) => ({
              ...item,
              _tipo: 'STAFF'
            })
          );

        const mapa = new Map();

        [...profs, ...staffs].forEach(
          (item) => {
            const id =
              item.id ||
              item._id ||
              item.user_id ||
              item.usuario_id;

            if (id && !mapa.has(id)) {
              mapa.set(
                id,
                {
                  ...item,
                  id
                }
              );
            }
          }
        );

        setAvaliadores(
          [...mapa.values()]
        );
      }
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    }
  };

  const alternarParticipante = (id) => {
    setParticipanteIds(
      (atual) =>
        atual.includes(id)
          ? atual.filter(
              (item) =>
                item !== id
            )
          : [...atual, id]
    );
  };

  const salvarParticipantes = async () => {
    if (!selecionada?.id) return;

    setSavingParticipantes(true);

    try {
      await api.put(
        `/seletivas/${selecionada.id}/participantes`,
        {
          aluno_ids: participanteIds
        }
      );

      toast.success(
        'Participantes atualizados'
      );

      await carregarDetalhes(
        selecionada
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setSavingParticipantes(false);
    }
  };

  const adicionarExterno = async (e) => {
    e.preventDefault();

    if (!selecionada?.id) return;

    if (!externoForm.nome.trim()) {
      toast.error(
        'Informe o nome do atleta.'
      );
      return;
    }

    setSavingExterno(true);

    try {
      await api.post(
        `/seletivas/${selecionada.id}/participantes/externo`,
        {
          nome: externoForm.nome.trim(),
          data_nascimento:
            externoForm.data_nascimento,
          sexo: externoForm.sexo,
          telefone:
            externoForm.telefone.trim(),
          observacao:
            externoForm.observacao.trim()
        }
      );

      toast.success(
        'Atleta externo adicionado'
      );

      setExternoDialog(false);

      setExternoForm({
        nome: '',
        data_nascimento: '',
        sexo: '',
        telefone: '',
        observacao: ''
      });

      await carregarDetalhes(
        selecionada
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setSavingExterno(false);
    }
  };

  const alternarAvaliador = (id) => {
    setProvaForm(
      (atual) => ({
        ...atual,
        avaliador_ids:
          atual.avaliador_ids.includes(id)
            ? atual.avaliador_ids.filter(
                (item) =>
                  item !== id
              )
            : [
                ...atual.avaliador_ids,
                id
              ]
      })
    );
  };

  const criarProva = async (e) => {
    e.preventDefault();

    if (!provaForm.nome.trim()) {
      toast.error(
        'Informe o nome da prova'
      );
      return;
    }

    if (
      provaForm.avaliador_ids.length === 0
    ) {
      toast.error(
        'Escolha pelo menos um avaliador'
      );
      return;
    }

    setSavingProva(true);

    try {
      const tipo =
        provaForm.tipo_resultado;

      const payload = {
        nome:
          provaForm.nome.trim(),

        descricao:
          provaForm.descricao.trim(),

        instrucoes:
          provaForm.instrucoes.trim(),

        tipo_resultado:
          tipo,

        unidade:
          ['tempo', 'medida', 'quantidade']
            .includes(tipo)
              ? provaForm.unidade.trim()
              : '',

        criterio_melhor:
          ['tempo', 'medida', 'quantidade', 'nota']
            .includes(tipo)
              ? provaForm.criterio_melhor
              : '',

        numero_tentativas:
          Math.max(
            1,
            Number(
              provaForm.numero_tentativas
            ) || 1
          ),

        conceitos:
          tipo === 'conceito'
            ? provaForm.conceitos
                .split(',')
                .map(
                  (x) =>
                    x.trim()
                )
                .filter(Boolean)
            : null,

        nota_minima:
          tipo === 'nota'
            ? Number(
                provaForm.nota_minima
              )
            : null,

        nota_maxima:
          tipo === 'nota'
            ? Number(
                provaForm.nota_maxima
              )
            : null,

        avaliador_ids:
          provaForm.avaliador_ids
      };

      await api.post(
        `/seletivas/${selecionada.id}/provas`,
        payload
      );

      toast.success(
        'Prova criada'
      );

      setProvaDialog(false);

      setProvaForm({
        nome: '',
        descricao: '',
        instrucoes: '',
        tipo_resultado: 'tempo',
        unidade: 's',
        criterio_melhor: 'menor',
        numero_tentativas: 1,
        conceitos: 'Excelente,Bom,Regular,Ruim',
        nota_minima: 0,
        nota_maxima: 10,
        avaliador_ids: []
      });

      await carregarDetalhes(
        selecionada
      );

      const painelResp =
        await api.get(
          `/seletivas/${selecionada.id}/painel`
        );

      setPainel(
        painelResp.data
      );
    } catch (err) {
      toast.error(
        formatApiError(err)
      );
    } finally {
      setSavingProva(false);
    }
  };

  const criar = async (e) => {
    e.preventDefault();

    if (!form.nome.trim()) {
      toast.error('Informe o nome da seletiva');
      return;
    }

    if (!form.categoria.trim()) {
      toast.error('Informe a categoria');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        sexo: form.sexo,
        categoria: form.categoria.trim(),
        ano_nascimento_inicio:
          form.ano_nascimento_inicio
            ? Number(form.ano_nascimento_inicio)
            : null,
        ano_nascimento_fim:
          form.ano_nascimento_fim
            ? Number(form.ano_nascimento_fim)
            : null,
        data: form.data || '',
        local: form.local.trim(),
        ativo: true
      };

      const response = await api.post(
        '/seletivas',
        payload
      );

      toast.success('Seletiva criada');

      setDialogOpen(false);
      setForm(formInicial);

      await carregar();

      if (response.data?.id) {
        abrirSeletiva(response.data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (selecionada && provaOperacao) {
    return (
      <SeletivaProvaOperacao
        seletiva={selecionada}
        prova={provaOperacao}
        onVoltar={() => {
          setProvaOperacao(null);
        }}
        onAtualizar={async () => {
          await carregarDetalhes(
            selecionada
          );
        }}
      />
    );
  }

  if (selecionada) {
    const provasPainel =
      provas.length > 0
        ? provas
        : (
            painel?.provas ||
            painel?.itens ||
            []
          );

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setSelecionada(null);
              setPainel(null);
              setProvaOperacao(null);
            }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold truncate">
              {selecionada.nome}
            </h1>

            <div className="flex flex-wrap gap-2 mt-1">
              <Badge variant="secondary">
                {selecionada.categoria}
              </Badge>

              <Badge variant="outline">
                {nomeSexo(selecionada.sexo)}
              </Badge>

              <Badge variant="outline">
                {nomeStatus(selecionada.status)}
              </Badge>
            </div>
          </div>

          {podeAdministrar && (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={exportarExcel}
                disabled={acaoSeletiva !== null}
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Excel
              </Button>

              {selecionada.status !== 'finalizada' && (
                <Button
                  variant="outline"
                  onClick={finalizarSeletiva}
                  disabled={acaoSeletiva !== null}
                >
                  {acaoSeletiva === 'finalizar' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}

                  Finalizar seletiva
                </Button>
              )}

              <Button
                variant="destructive"
                onClick={apagarSeletiva}
                disabled={acaoSeletiva !== null}
              >
                {acaoSeletiva === 'apagar' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}

                Apagar seletiva
              </Button>
            </div>
          )}
        </div>

        {loadingPainel ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-zinc-400">
                    Provas
                  </div>
                  <div className="text-2xl font-bold mt-1">
                    {provasPainel.length}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-zinc-400">
                    Categoria
                  </div>
                  <div className="font-semibold mt-1">
                    {selecionada.categoria}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-zinc-400">
                    Sexo
                  </div>
                  <div className="font-semibold mt-1">
                    {nomeSexo(selecionada.sexo)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-zinc-400">
                    Status
                  </div>
                  <div className="font-semibold mt-1">
                    {nomeStatus(selecionada.status)}
                  </div>
                </CardContent>
              </Card>
            </div>

            {selecionada.descricao && (
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-zinc-300">
                    {selecionada.descricao}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2 overflow-x-auto pb-1">
              <Button
                variant={aba === 'provas' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAba('provas')}
              >
                Provas
              </Button>

              {podeAdministrar && (
                <Button
                  variant={aba === 'participantes' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAba('participantes')}
                >
                  Participantes
                </Button>
              )}
            </div>

            {aba === 'participantes' && podeAdministrar ? (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Participantes
                    </h2>

                    <p className="text-sm text-zinc-400">
                      {participanteIds.length} selecionado(s)
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setExternoDialog(true)
                      }
                    >
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Atleta não cadastrado
                    </Button>

                    <Button
                      onClick={salvarParticipantes}
                      disabled={savingParticipantes}
                    >
                      {savingParticipantes ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}

                      Salvar
                    </Button>
                  </div>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <Label>
                      Pesquisar aluno
                    </Label>

                    <Input
                      className="mt-2"
                      value={buscaParticipante}
                      onChange={(e) =>
                        setBuscaParticipante(
                          e.target.value
                        )
                      }
                      placeholder="Digite o nome do aluno..."
                    />

                    <div className="text-xs text-zinc-400 mt-2">
                      Mostrando {listaParticipantes.length} atleta(s)
                    </div>
                  </CardContent>
                </Card>

                {listaParticipantes.length === 0 ? (
                  <Card>
                    <CardContent className="py-10 text-center text-zinc-400">
                      Nenhum atleta encontrado.
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {listaParticipantes.map((aluno) => {
                      const id =
                        aluno.id ||
                        aluno.aluno_id;

                      const selecionado =
                        participanteIds.includes(id);

                      const externo =
                        aluno.tipo_participante ===
                        'externo';

                      return (
                        <Card
                          key={id}
                          className={
                            selecionado
                              ? 'cursor-pointer border-emerald-500/50'
                              : 'cursor-pointer'
                          }
                          onClick={() =>
                            alternarParticipante(id)
                          }
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div
                                className={
                                  selecionado
                                    ? 'w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0'
                                    : 'w-6 h-6 rounded-full border border-zinc-600 shrink-0'
                                }
                              >
                                {selecionado && (
                                  <Check className="w-4 h-4 text-white" />
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">
                                  {aluno.name ||
                                    aluno.aluno_nome ||
                                    'Atleta'}
                                </div>

                                <div className="flex flex-wrap gap-1 mt-2">
                                  {externo ? (
                                    <Badge variant="outline">
                                      Externo
                                    </Badge>
                                  ) : (
                                    <>
                                      <Badge variant="outline">
                                        Cadastrado
                                      </Badge>

                                      {aluno.elegivel && (
                                        <Badge>
                                          Elegível
                                        </Badge>
                                      )}
                                    </>
                                  )}
                                </div>

                                {aluno.data_nascimento && (
                                  <div className="text-xs text-zinc-400 mt-2">
                                    {aluno.data_nascimento}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      Provas
                    </h2>

                    <p className="text-sm text-zinc-400">
                      {podeAdministrar
                        ? 'Configure as provas e seus avaliadores.'
                        : 'Estas são as provas atribuídas a você.'}
                    </p>
                  </div>

                  {podeAdministrar && (
                    <Button
                      onClick={() =>
                        setProvaDialog(true)
                      }
                    >
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Nova prova
                    </Button>
                  )}
                </div>

                {provasPainel.length === 0 ? (
                  <Card>
                    <CardContent className="py-10 text-center">
                      <ClipboardCheck className="w-9 h-9 mx-auto mb-3 text-zinc-500" />

                      <div className="font-medium">
                        Nenhuma prova disponível
                      </div>

                      <div className="text-sm text-zinc-400 mt-1">
                        {podeAdministrar
                          ? 'Cadastre a primeira prova da seletiva.'
                          : 'Você ainda não possui prova atribuída.'}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-3">
                    {provasPainel.map((prova) => (
                      <Card
                        key={prova.id}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                        onClick={() =>
                          setProvaOperacao(prova)
                        }
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-semibold">
                                {prova.nome}
                              </div>

                              <div className="flex gap-2 flex-wrap mt-2">
                                <Badge variant="outline">
                                  {prova.tipo_resultado}
                                </Badge>

                                {prova.numero_tentativas && (
                                  <Badge variant="secondary">
                                    {prova.numero_tentativas} tentativa(s)
                                  </Badge>
                                )}
                              </div>

                              {prova.descricao && (
                                <div className="text-sm text-zinc-400 mt-2">
                                  {prova.descricao}
                                </div>
                              )}
                            </div>

                            <ChevronRight className="w-5 h-5 text-zinc-500" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Dialog
              open={externoDialog}
              onOpenChange={setExternoDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Adicionar atleta não cadastrado
                  </DialogTitle>
                </DialogHeader>

                <form
                  onSubmit={adicionarExterno}
                  className="space-y-4"
                >
                  <div>
                    <Label>Nome completo *</Label>

                    <Input
                      value={externoForm.nome}
                      onChange={(e) =>
                        setExternoForm({
                          ...externoForm,
                          nome: e.target.value
                        })
                      }
                      placeholder="Nome do atleta"
                    />
                  </div>

                  <div>
                    <Label>Data de nascimento</Label>

                    <Input
                      type="date"
                      value={externoForm.data_nascimento}
                      onChange={(e) =>
                        setExternoForm({
                          ...externoForm,
                          data_nascimento:
                            e.target.value
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label>Sexo</Label>

                    <Select
                      value={
                        externoForm.sexo ||
                        undefined
                      }
                      onValueChange={(valor) =>
                        setExternoForm({
                          ...externoForm,
                          sexo: valor
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Não informado" />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="masculino">
                          Masculino
                        </SelectItem>

                        <SelectItem value="feminino">
                          Feminino
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Telefone</Label>

                    <Input
                      value={externoForm.telefone}
                      onChange={(e) =>
                        setExternoForm({
                          ...externoForm,
                          telefone: e.target.value
                        })
                      }
                      placeholder="Opcional"
                    />
                  </div>

                  <div>
                    <Label>Observação</Label>

                    <Textarea
                      value={externoForm.observacao}
                      onChange={(e) =>
                        setExternoForm({
                          ...externoForm,
                          observacao: e.target.value
                        })
                      }
                      placeholder="Opcional"
                    />
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setExternoDialog(false)
                      }
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="submit"
                      disabled={savingExterno}
                    >
                      {savingExterno && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}

                      Adicionar participante
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog
              open={provaDialog}
              onOpenChange={setProvaDialog}
            >
              <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    Nova prova
                  </DialogTitle>
                </DialogHeader>

                <form
                  onSubmit={criarProva}
                  className="space-y-4"
                >
                  <div>
                    <Label>Nome *</Label>

                    <Input
                      value={provaForm.nome}
                      onChange={(e) =>
                        setProvaForm({
                          ...provaForm,
                          nome: e.target.value
                        })
                      }
                      placeholder="Ex.: Sprint 20 metros"
                    />
                  </div>

                  <div>
                    <Label>Descrição</Label>

                    <Textarea
                      value={provaForm.descricao}
                      onChange={(e) =>
                        setProvaForm({
                          ...provaForm,
                          descricao: e.target.value
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label>Instruções para o avaliador</Label>

                    <Textarea
                      value={provaForm.instrucoes}
                      onChange={(e) =>
                        setProvaForm({
                          ...provaForm,
                          instrucoes: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Tipo de resultado *</Label>

                      <Select
                        value={provaForm.tipo_resultado}
                        onValueChange={(valor) =>
                          setProvaForm({
                            ...provaForm,
                            tipo_resultado: valor,
                            criterio_melhor:
                              valor === 'tempo'
                                ? 'menor'
                                : 'maior'
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="tempo">
                            Tempo
                          </SelectItem>

                          <SelectItem value="medida">
                            Medida
                          </SelectItem>

                          <SelectItem value="quantidade">
                            Quantidade
                          </SelectItem>

                          <SelectItem value="conceito">
                            Conceito
                          </SelectItem>

                          <SelectItem value="nota">
                            Nota
                          </SelectItem>

                          <SelectItem value="observacao">
                            Observação
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Tentativas</Label>

                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={provaForm.numero_tentativas}
                        onChange={(e) =>
                          setProvaForm({
                            ...provaForm,
                            numero_tentativas:
                              e.target.value
                          })
                        }
                      />
                    </div>
                  </div>

                  {['tempo', 'medida', 'quantidade'].includes(
                    provaForm.tipo_resultado
                  ) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Unidade</Label>

                        <Input
                          value={provaForm.unidade}
                          onChange={(e) =>
                            setProvaForm({
                              ...provaForm,
                              unidade: e.target.value
                            })
                          }
                          placeholder="s, cm, m, rep..."
                        />
                      </div>

                      <div>
                        <Label>Melhor resultado</Label>

                        <Select
                          value={provaForm.criterio_melhor}
                          onValueChange={(valor) =>
                            setProvaForm({
                              ...provaForm,
                              criterio_melhor: valor
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value="menor">
                              Menor valor
                            </SelectItem>

                            <SelectItem value="maior">
                              Maior valor
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {provaForm.tipo_resultado === 'conceito' && (
                    <div>
                      <Label>
                        Conceitos, do melhor para o pior
                      </Label>

                      <Input
                        value={provaForm.conceitos}
                        onChange={(e) =>
                          setProvaForm({
                            ...provaForm,
                            conceitos: e.target.value
                          })
                        }
                        placeholder="Excelente,Bom,Regular,Ruim"
                      />
                    </div>
                  )}

                  {provaForm.tipo_resultado === 'nota' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Nota mínima</Label>

                        <Input
                          type="number"
                          value={provaForm.nota_minima}
                          onChange={(e) =>
                            setProvaForm({
                              ...provaForm,
                              nota_minima:
                                e.target.value
                            })
                          }
                        />
                      </div>

                      <div>
                        <Label>Nota máxima</Label>

                        <Input
                          type="number"
                          value={provaForm.nota_maxima}
                          onChange={(e) =>
                            setProvaForm({
                              ...provaForm,
                              nota_maxima:
                                e.target.value
                            })
                          }
                        />
                      </div>

                      <div>
                        <Label>Melhor</Label>

                        <Select
                          value={provaForm.criterio_melhor}
                          onValueChange={(valor) =>
                            setProvaForm({
                              ...provaForm,
                              criterio_melhor:
                                valor
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value="maior">
                              Maior
                            </SelectItem>

                            <SelectItem value="menor">
                              Menor
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>
                      Avaliador(es) *
                    </Label>

                    {avaliadores.length === 0 ? (
                      <div className="text-sm text-zinc-400 border rounded-md p-3 mt-2">
                        Nenhum professor ou STAFF encontrado.
                      </div>
                    ) : (
                      <div className="border rounded-md divide-y mt-2 max-h-52 overflow-y-auto">
                        {avaliadores.map((avaliador) => {
                          const marcado =
                            provaForm.avaliador_ids.includes(
                              avaliador.id
                            );

                          return (
                            <button
                              type="button"
                              key={avaliador.id}
                              onClick={() =>
                                alternarAvaliador(
                                  avaliador.id
                                )
                              }
                              className="w-full p-3 text-left flex items-center gap-3 hover:bg-zinc-900"
                            >
                              <div
                                className={
                                  marcado
                                    ? 'w-5 h-5 rounded bg-emerald-500 flex items-center justify-center'
                                    : 'w-5 h-5 rounded border border-zinc-600'
                                }
                              >
                                {marcado && (
                                  <Check className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>

                              <div>
                                <div className="font-medium">
                                  {avaliador.nome ||
                                    avaliador.nome_completo ||
                                    avaliador.email}
                                </div>

                                <div className="text-xs text-zinc-400">
                                  {avaliador._tipo}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setProvaDialog(false)
                      }
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="submit"
                      disabled={savingProva}
                    >
                      {savingProva && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}

                      Criar prova
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            Seletivas
          </h1>

          <p className="text-sm text-zinc-400 mt-1">
            {podeAdministrar
              ? 'Crie e acompanhe avaliações de atletas.'
              : 'Acesse as seletivas e provas atribuídas a você.'}
          </p>
        </div>

        {podeAdministrar && (
          <Button
            onClick={() => {
              setForm(formInicial);
              setDialogOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova seletiva
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="w-7 h-7 animate-spin" />
        </div>
      ) : seletivas.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-zinc-500" />

            <div className="font-semibold">
              Nenhuma seletiva
            </div>

            <div className="text-sm text-zinc-400 mt-1">
              {podeAdministrar
                ? 'Crie a primeira seletiva para começar.'
                : 'Nenhuma avaliação foi atribuída a você.'}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {seletivas.map((item) => (
            <Card
              key={item.id}
              className="cursor-pointer hover:border-zinc-600 transition-colors"
              onClick={() => abrirSeletiva(item)}
            >
              <CardContent className="p-5">
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-lg truncate">
                      {item.nome}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="secondary">
                        {item.categoria}
                      </Badge>

                      <Badge variant="outline">
                        {nomeSexo(item.sexo)}
                      </Badge>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 shrink-0 text-zinc-500" />
                </div>

                {item.descricao && (
                  <p className="text-sm text-zinc-400 mt-4 line-clamp-2">
                    {item.descricao}
                  </p>
                )}

                <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-xs text-zinc-500">
                  {item.data && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {item.data}
                    </span>
                  )}

                  {item.local && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {item.local}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Nova seletiva
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={criar}
            className="space-y-4"
          >
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.nome}
                onChange={(e) =>
                  setForm({
                    ...form,
                    nome: e.target.value
                  })
                }
                placeholder="Ex.: Seletiva Estadual 2026"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) =>
                  setForm({
                    ...form,
                    descricao: e.target.value
                  })
                }
                placeholder="Informações gerais da seletiva"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sexo *</Label>

                <Select
                  value={form.sexo}
                  onValueChange={(valor) =>
                    setForm({
                      ...form,
                      sexo: valor
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="misto">
                      Misto
                    </SelectItem>

                    <SelectItem value="masculino">
                      Masculino
                    </SelectItem>

                    <SelectItem value="feminino">
                      Feminino
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Categoria *</Label>
                <Input
                  value={form.categoria}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      categoria: e.target.value
                    })
                  }
                  placeholder="Ex.: Sub-15"
                />
              </div>
            </div>

            <div>
              <Label>
                Ano de nascimento
              </Label>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  placeholder="De"
                  value={form.ano_nascimento_inicio}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ano_nascimento_inicio:
                        e.target.value
                    })
                  }
                />

                <Input
                  type="number"
                  placeholder="Até"
                  value={form.ano_nascimento_fim}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ano_nascimento_fim:
                        e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      data: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <Label>Local</Label>
                <Input
                  value={form.local}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      local: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setDialogOpen(false)
                }
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={saving}
              >
                {saving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                Criar seletiva
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
